import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Send, Sparkles, Bot, MessageCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import { SaveNote } from "@/components/SaveNote";
import { GuidanceNote } from "@/components/Trust";
import { VoiceInput } from "@/components/VoiceInput";

const suggestionKeys = ["s1", "s2", "s3", "s4", "s5"];

function Md({ text }) {
  const lines = String(text || "").replace(/^#{1,6}\s+/gm, "").split("\n");
  return lines.map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : p.replace(/\*([^*]+)\*/g, "$1"));
    return <span key={i}>{parts}{i < lines.length - 1 && "\n"}</span>;
  });
}

export default function Coach() {
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "ai_skills" ? "ai_skills" : "coach";
  const setMode = (m) => setParams(m === "ai_skills" ? { mode: "ai_skills" } : {});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef();
  const navigate = useNavigate();
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/coach/history").then(({ data }) => { setMessages(data.messages || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const runAction = async (a) => {
    if (a.type === "start_quest") {
      try { await api.post("/quests/start", { quest_key: a.quest_key }); toast.success(t("questStarted")); navigate("/app/missions"); }
      catch (e) { toast.error(apiError(e)); }
    } else navigate(a.to);
  };

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || busy || voiceBusy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/coach/message", { message: msg, lang, mode });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, actions: data.actions || [] }]);
    } catch (e) {
      if (e?.response?.status === 429) {
        setMessages((m) => [...m, { role: "assistant", content: apiError(e) }]);
      } else if (e?.response?.status === 402) { navigate("/paywall"); }
      else setMessages((m) => [...m, { role: "assistant", content: t("coachErr") }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex h-[calc(100vh-11rem)] flex-col md:h-[calc(100vh-13rem)]">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white"><Sparkles className="h-5 w-5" /></div>
        <div>
          <h1 className="font-display text-xl font-extrabold">{mode === "ai_skills" ? "AI Skills Coach" : t("coachTitle")}</h1>
          <p className="text-xs text-muted-foreground" data-testid="coach-subtitle">{t("coachSub2")}{lang === "hi" ? " · Hinglish mode" : ""}</p>
        </div>
      </div>

      <div className="mb-3 flex gap-1.5" data-testid="coach-mode-toggle">
        <button onClick={() => setMode("coach")} data-testid="coach-mode-coach" className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold ${mode === "coach" ? "bg-slate-900 text-white" : "border border-border hover:bg-secondary"}`}><MessageCircle className="h-3.5 w-3.5" /> Career Coach</button>
        <button onClick={() => setMode("ai_skills")} data-testid="coach-mode-ai-skills" className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold ${mode === "ai_skills" ? "bg-slate-900 text-white" : "border border-border hover:bg-secondary"}`}><Bot className="h-3.5 w-3.5" /> AI Skills Coach</button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto no-scrollbar pb-4" data-testid="coach-messages">
        {loaded && messages.length === 0 && (
          <div className="rounded-3xl border border-border bg-card p-6 text-center">
            <p className="font-display text-lg font-bold">{t("coachEmpty")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("coachEmptySub")}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {suggestionKeys.map((k) => (
                <button key={k} onClick={() => send(t(k))} data-testid={`coach-suggestion-${k}`}
                  className="rounded-full border border-border bg-secondary px-3 py-1.5 text-xs font-semibold transition-colors hover:border-indigo-400 hover:text-indigo-600">{t(k)}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div style={{ fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif" }} data-testid={`coach-msg-${m.role}-${i}`} className={`max-w-[85%] whitespace-pre-wrap rounded-3xl px-4 py-3 text-[15px] leading-relaxed ${
              m.role === "user" ? "bg-indigo-600 text-white" : "border border-border bg-card"}`}>
              <Md text={m.content} />
              {m.role === "assistant" && <div className="mt-3"><SaveNote testId={`coach-save-note-${i}`} title="Advice from my Career Coach" content={m.content} source="coach" sourceRef={`${mode}-${i}`} category="advice" /></div>}
              {m.actions?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`coach-actions-${i}`}>
                  {m.actions.map((a, j) => (
                    <button key={j} onClick={() => runAction(a)} data-testid={`coach-action-${i}-${j}-${a.type}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700"><Zap className="h-3 w-3" /> {a.label}</button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}
        {busy && <div className="flex justify-start"><div className="rounded-3xl border border-border bg-card px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-indigo-600" /></div></div>}
        <div ref={endRef} />
      </div>

      <GuidanceNote compact className="mb-1.5" />
      <div className="flex items-center gap-2 border-t border-border pt-3">
        <input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder={mode === "ai_skills" ? "e.g. How do I use ChatGPT to learn SQL in 2 weeks?" : t("coachPh")}
          className="min-w-0 flex-1 rounded-full border border-input bg-card px-4 py-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" />
        <VoiceInput context="coach" testId="coach-voice" disabled={busy} onBusyChange={setVoiceBusy} onUse={text => setInput(current => [current, text].filter(Boolean).join(" "))} />
        <button onClick={() => send()} disabled={busy || voiceBusy} data-testid="coach-send-btn"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-600 text-white transition-colors hover:bg-indigo-700 disabled:opacity-60">
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
